
import { Badge } from "@/components/ui/badge";
import { TableCell, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { EtsyOrder } from "@/types/etsy";
import OrderDetails from "@/components/OrderDetails";
import EditOrderDialog from "@/components/EditOrderDialog";
import { useCurrency } from "@/contexts/CurrencyContext";
import { getStatusColor, formatAddress, hasMultipleItems, getItemsText } from "@/utils/orderUtils";
import { getCountryName, getCountryFlag } from "@/utils/countryUtils";

interface OrdersTableRowProps {
  order: EtsyOrder;
  index: number;
  isExpanded: boolean;
  onToggleExpansion: (receiptId: string) => void;
  onRowClick: (receiptId: string, event: React.MouseEvent) => void;
  onOrderUpdate?: (updatedOrder: EtsyOrder) => void;
}

const OrdersTableRow = ({ 
  order, 
  index, 
  isExpanded, 
  onToggleExpansion, 
  onRowClick, 
  onOrderUpdate 
}: OrdersTableRowProps) => {
  const { formatPrice } = useCurrency();
  const isMultiItem = hasMultipleItems(order);

  const handleOrderUpdate = (updatedOrder: EtsyOrder) => {
    console.log("[OrdersTableRow] Forwarding updated order to parent", updatedOrder);
    if (onOrderUpdate) {
      onOrderUpdate(updatedOrder);
    }
  };

  // Get full country name and flag
  const countryName = getCountryName(order.country);
  const countryFlag = getCountryFlag(order.country);

  return (
    <>
      <TableRow 
        key={`${order.receiptId}-${index}`}
        className="hover:bg-gray-50 cursor-pointer"
        onClick={(e) => onRowClick(order.receiptId, e)}
      >
        <TableCell>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpansion(order.receiptId);
            }}
            className="p-1 hover:bg-gray-100 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </TableCell>
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            {order.receiptId}
            {isMultiItem && (
              <Badge variant="outline" className="text-xs">
                {order.items!.length} items
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>{order.customerName}</TableCell>
        <TableCell className="max-w-xs">
          <div className="truncate" title={getItemsText(order)}>
            {getItemsText(order)}
          </div>
        </TableCell>
        <TableCell className="max-w-xs truncate" title={formatAddress(order)}>
          {formatAddress(order)}
        </TableCell>
        <TableCell>{order.city}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="text-lg">{countryFlag}</span>
            <span>{countryName}</span>
          </div>
        </TableCell>
        <TableCell>
          <Badge 
            variant="outline" 
            className={getStatusColor(order.status)}
          >
            {order.status}
          </Badge>
        </TableCell>
        <TableCell>Etsy Payment</TableCell>
        <TableCell>
          {order.subtotalAmount !== undefined && order.currencyCode 
            ? formatPrice(order.subtotalAmount, order.currencyCode)
            : 'N/A'
          }
        </TableCell>
        <TableCell>
          {order.totalAmount !== undefined && order.currencyCode 
            ? formatPrice(order.totalAmount, order.currencyCode)
            : 'N/A'
          }
        </TableCell>
        <TableCell>{order.date}</TableCell>
        <TableCell>
          <EditOrderDialog 
            order={order} 
            onOrderUpdate={handleOrderUpdate}
          />
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={13} className="p-0">
            <OrderDetails order={order} onOrderUpdate={handleOrderUpdate} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default OrdersTableRow;
